/**
 * @fileoverview Pure ranking for violation code-set index entries.
 * @module inspection-renderer/violations
 *
 * Responsibilities:
 * - Define the shared searchable code-set entry contract.
 * - Match and rank entries across both host index schemas.
 *
 * Dependencies:
 * - None.
 *
 * Architecture:
 * - Pure package logic; hosts supply only Dexie-backed index rows.
 *
 * Notes:
 * - Search includes mobile snippets and tenant's richer body/display/search text.
 */

/**
 * @contract ViolationCodeSetIndexEntry
 * Purpose: Minimal union of searchable violation index fields used by the renderer.
 * Fields: Code identity, title, citation, active status, and optional host text fields.
 * Rules: All textual search fields are optional; inactive and deleted rows are excluded.
 * Sync: Supplied by a host from its local Dexie index.
 */
export interface ViolationCodeSetIndexEntry {
  _id: string;
  codeSetId: string;
  code: string;
  title: string;
  codeReference?: string;
  contentSnippet?: string;
  bodyText?: string;
  displayText?: string;
  searchText?: string;
  category?: string;
  chapterTitle?: string;
  isActive: boolean;
  _deleted?: boolean;
  source?: string;
  createdAt?: string;
  updatedAt?: string;
}

function normalize(value: string | undefined): string {
  return (value ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
}

function scoreEntry(entry: ViolationCodeSetIndexEntry, query: string): number {
  const q = normalize(query);
  if (!q) return 1;
  const fields = {
    code: normalize(entry.code),
    title: normalize(entry.title),
    reference: normalize(entry.codeReference),
    snippet: normalize(entry.contentSnippet),
    body: normalize(entry.bodyText),
    display: normalize(entry.displayText),
    search: normalize(entry.searchText),
    category: normalize(entry.category),
    chapter: normalize(entry.chapterTitle),
  };
  const searchable = Object.values(fields).join(' ');
  const tokens = q.split(' ').filter(Boolean);
  if (!tokens.every((token) => searchable.includes(token))) return 0;

  let score = 1;
  if (fields.code === q) score += 1000;
  if (fields.code.startsWith(q)) score += 800;
  if (fields.code.includes(q)) score += 600;
  if (fields.reference.includes(q)) score += 450;
  if (fields.title === q) score += 400;
  if (fields.title.startsWith(q)) score += 300;
  if (fields.title.includes(q)) score += 220;
  if (fields.category.includes(q)) score += 140;
  if (fields.chapter.includes(q)) score += 120;
  if (fields.snippet.includes(q)) score += 90;
  if (fields.display.includes(q)) score += 80;
  if (fields.body.includes(q)) score += 70;
  if (fields.search.includes(q)) score += 60;
  for (const token of tokens) {
    if (fields.code.includes(token)) score += 80;
    if (fields.reference.includes(token)) score += 55;
    if (fields.title.includes(token)) score += 45;
    if (fields.category.includes(token)) score += 25;
    if (fields.chapter.includes(token)) score += 20;
    if (fields.snippet.includes(token)) score += 12;
    if (fields.display.includes(token) || fields.body.includes(token) || fields.search.includes(token)) score += 10;
  }
  return score;
}

/**
 * @process rankViolationCodeSetEntries
 * Steps: Filter inactive rows, match all query tokens across union text fields, rank, and cap results.
 * Inputs: Dexie index entries, query string, and optional result limit.
 * Outputs: Matching rows in descending relevance order.
 * Failure Handling: Empty query returns active entries in stable code order.
 * Idempotency: REQUIRED.
 */
export function rankViolationCodeSetEntries(
  entries: ViolationCodeSetIndexEntry[],
  query: string,
  limit = 20,
): ViolationCodeSetIndexEntry[] {
  return entries
    .filter((entry) => entry.isActive && !entry._deleted)
    .map((entry) => ({ entry, score: scoreEntry(entry, query) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score || a.entry.code.localeCompare(b.entry.code, undefined, { numeric: true }))
    .slice(0, limit)
    .map(({ entry }) => entry);
}
